import type { TimelineEvent } from "../../../experience-shell/workflows/timeline";

export interface FieldDiff {
  readonly label: string;
  readonly before: string;
  readonly after: string;
}

/**
 * Single boundary parse for change-style activities. The Einstein recorder
 * captures every payload via `ActivityData.Capture`, which nests the change
 * object under `data.raw`; the ledger client serializes that node with the
 * default options, so the inner keys arrive PascalCase (`Field`/`Before`/
 * `After`, `BeforeTitle`/`AfterTitle`). Keys are read case-insensitively so
 * the view stays correct against that wire shape and against a later
 * camelCase normalization. No-op records (before === after) are dropped.
 */
export function describeChange(event: TimelineEvent): readonly FieldDiff[] {
  const raw = unwrapRaw(event.data);
  if (raw === null) return [];

  switch (event.activityType) {
    case "workitem.edited":
    case "note.edited":
      return singleFieldDiff(raw, "Body", formatText);
    case "workitem.snoozed":
    case "workitem.rescheduled":
      return singleFieldDiff(raw, "Due date", formatDueValue);
    case "workflow.edited": {
      const diffs: FieldDiff[] = [];
      pushDiff(diffs, "Title", getCI(raw, "beforeTitle"), getCI(raw, "afterTitle"), formatText);
      pushDiff(diffs, "Kind", getCI(raw, "beforeKind"), getCI(raw, "afterKind"), formatText);
      return diffs;
    }
    case "queue.renamed":
      return namedFieldDiff(raw, "Name", "oldName", "newName");
    case "queue.reparented":
      return namedFieldDiff(raw, "Parent queue", "oldParentQueueId", "newParentQueueId");
    case "queue.owner.changed":
      return namedFieldDiff(raw, "Management group", "from", "to");
    default:
      return [];
  }
}

function namedFieldDiff(
  raw: Record<string, unknown>,
  label: string,
  beforeKey: string,
  afterKey: string,
): readonly FieldDiff[] {
  const diffs: FieldDiff[] = [];
  pushDiff(diffs, label, getCI(raw, beforeKey), getCI(raw, afterKey), formatText);
  return diffs;
}

function singleFieldDiff(
  raw: Record<string, unknown>,
  label: string,
  format: (value: unknown) => string,
): readonly FieldDiff[] {
  const diffs: FieldDiff[] = [];
  pushDiff(diffs, label, getCI(raw, "before"), getCI(raw, "after"), format);
  return diffs;
}

function pushDiff(
  sink: FieldDiff[],
  label: string,
  before: unknown,
  after: unknown,
  format: (value: unknown) => string,
): void {
  const b = format(before);
  const a = format(after);
  if (b === a) return;
  sink.push({ label, before: b, after: a });
}

export function unwrapRaw(data: unknown): Record<string, unknown> | null {
  if (data === null || data === undefined || typeof data !== "object") return null;
  const obj = data as Record<string, unknown>;
  const raw = obj["raw"];
  if (raw !== null && typeof raw === "object") return raw as Record<string, unknown>;
  // Change-style payloads authored before the Capture-only convention land
  // at the top level.
  return obj;
}

export function getCI(obj: Record<string, unknown>, key: string): unknown {
  if (key in obj) return obj[key];
  const lower = key.toLowerCase();
  for (const k of Object.keys(obj)) {
    if (k.toLowerCase() === lower) return obj[k];
  }
  return undefined;
}

export function queueMemberLabel(event: TimelineEvent): string {
  const raw = unwrapRaw(event.data);
  if (raw === null) return "a member";
  const displayName = getCI(raw, "memberDisplayName");
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName;
  }
  const userId = getCI(raw, "memberUserId");
  return typeof userId === "string" && userId.trim().length > 0 ? userId : "a member";
}

export function queueMemberCountLabel(event: TimelineEvent, key: string): string {
  const raw = unwrapRaw(event.data);
  const value = raw === null ? undefined : getCI(raw, key);
  const count = Array.isArray(value) ? value.length : 0;
  if (count === 0) return "queue members";
  return `${String(count)} queue ${count === 1 ? "member" : "members"}`;
}

function formatText(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value.length > 0 ? value : "(empty)";
  return String(value);
}

function formatDueValue(value: unknown): string {
  if (value === null || value === undefined) return "No due date";
  if (typeof value === "string") {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
    }
    return value;
  }
  return String(value);
}

export function workItemNoun(type: string): string {
  switch (type) {
    case "message":
      return "message";
    case "task":
      return "task";
    case "note":
      return "note";
    default:
      return "item";
  }
}

export function indefiniteArticle(noun: string): string {
  if (noun.length === 0) return "a";
  return /^[aeiou]/i.test(noun) ? "an" : "a";
}

/**
 * Actor name precedence: the ledger snapshot, then a display name inside the
 * captured payload, then the work-item creator, then the actor type or id.
 */
export function actorLabel(event: TimelineEvent): string {
  const ledgerDisplayName = event.actorDisplayName?.trim();
  if (ledgerDisplayName) return ledgerDisplayName;

  const fromData = extractDisplayNameFromData(event.data);
  if (fromData !== null) return fromData;

  if (event.workItem !== null && event.workItem !== undefined) {
    if (
      event.workItem.createdByDisplayName !== null &&
      event.workItem.createdByDisplayName !== undefined &&
      event.workItem.createdByDisplayName.length > 0
    ) {
      return event.workItem.createdByDisplayName;
    }
  }

  if (event.actorType === "system") return "System";
  return event.actorId.length > 0 ? event.actorId : "Unknown actor";
}

function extractDisplayNameFromData(data: unknown): string | null {
  if (data === null || data === undefined || typeof data !== "object") return null;
  const obj = data as Record<string, unknown>;
  // Captured payloads arrive as `{ raw: { ...fields } }`; change-style
  // payloads may sit at the top level. Both shapes are checked.
  const candidates: readonly unknown[] = [obj["raw"], obj["newValue"], obj];
  for (const candidate of candidates) {
    if (candidate === null || typeof candidate !== "object") continue;
    const c = candidate as Record<string, unknown>;
    const value =
      (typeof c["createdByDisplayName"] === "string" ? c["createdByDisplayName"] : null) ??
      (typeof c["completedByDisplayName"] === "string" ? c["completedByDisplayName"] : null) ??
      (typeof c["createdByName"] === "string" ? c["createdByName"] : null) ??
      (typeof c["actorDisplayName"] === "string" ? c["actorDisplayName"] : null);
    if (typeof value === "string" && value.length > 0) return value;
  }
  return null;
}
